import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import BlockEditor from "@/components/BlockEditor";
import Uploader from "@/components/Uploader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

const empty = (subject) => ({
  subject: subject || "matematicas",
  title: "", description: "", cover_image: "", blocks: [], published: true, order: 0,
});

const migrate = (o) => {
  if (Array.isArray(o.blocks) && o.blocks.length) return { ...empty(o.subject), ...o };
  const blocks = [];
  if (o.video_url) blocks.push({ id: uid(), type: "video", url: o.video_url });
  if (o.content) blocks.push({ id: uid(), type: "text", html: o.content });
  if (o.presentation) blocks.push({ id: uid(), type: "powerpoint", url: o.presentation, name: o.presentation_name || "" });
  (o.slides || []).forEach((s) => {
    const html = (s.title ? `<h3>${s.title}</h3>` : "") + (s.content || "");
    if (html) blocks.push({ id: uid(), type: "text", html });
    if (s.image) blocks.push({ id: uid(), type: "image", url: s.image, caption: "", align: "center", width: 100 });
  });
  (o.practice || []).forEach((p) => blocks.push({ id: uid(), type: "practice", problem: p.problem || "", solution: p.solution || "" }));
  return { ...empty(o.subject), ...o, blocks };
};

export default function LessonEditor() {
  const { id } = useParams();
  const isNew = id === "nueva" || !id;
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty(params.get("subject")));
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/lessons/${id}`).then((r) => { setForm(migrate(r.data)); setLoading(false); }).catch(() => { toast.error("No se pudo cargar"); setLoading(false); });
    }
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    const payload = {
      subject: form.subject, title: form.title, description: form.description,
      cover_image: form.cover_image, blocks: form.blocks, published: form.published,
      order: Number(form.order) || 0,
    };
    try {
      if (isNew) await api.post("/lessons", payload);
      else await api.put(`/lessons/${id}`, payload);
      toast.success("Apartado guardado");
      nav(`/aprende/${form.subject}`);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="animate-spin text-nasa-blue" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-24 pb-10">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-ink-500 hover:text-nasa-blue text-sm font-semibold mb-6"><ArrowLeft size={16} /> Volver</button>

      <div className="flex items-center justify-between gap-4 mb-8 pb-4 border-b-2 border-paper-200">
        <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-nasa-blue">{isNew ? "Nuevo apartado" : "Editar apartado"}</h1>
        <Button onClick={save} disabled={saving} data-testid="save-lesson" className="bg-nasa-blue text-white hover:bg-nasa-navy font-semibold rounded-full">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-2" /> Guardar</>}
        </Button>
      </div>

      <div className="space-y-6">
        <div>
          <label className="eyebrow text-nasa-blue block mb-2">Disciplina</label>
          <div className="flex gap-2">
            {["matematicas", "fisica"].map((s) => (
              <button key={s} onClick={() => set("subject", s)} data-testid={`subject-pick-${s}`} className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${form.subject === s ? "bg-nasa-blue text-white border-nasa-blue" : "bg-white border-paper-300 text-ink-700 hover:border-nasa-blue"}`}>
                {s === "matematicas" ? "Matemáticas" : "Física"}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="eyebrow text-ink-500 block mb-2">Título *</label>
          <Input data-testid="lesson-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ej: Ecuaciones de segundo grado" />
        </div>

        <div>
          <label className="eyebrow text-ink-500 block mb-2">Descripción breve</label>
          <Textarea data-testid="lesson-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} />
        </div>

        <div>
          <label className="eyebrow text-ink-500 block mb-2">Imagen de portada</label>
          <Uploader value={form.cover_image} onChange={(v) => set("cover_image", v)} testid="lesson-cover" />
        </div>

        <div>
          <label className="eyebrow text-nasa-blue block mb-3">Contenido del apartado</label>
          <p className="text-xs text-ink-400 mb-3">Añade y reordena bloques de texto, imágenes, vídeos, presentaciones y problemas de práctica. Arrastra con ⠿ o usa las flechas ↑↓.</p>
          <BlockEditor value={form.blocks} onChange={(b) => set("blocks", b)} allowPractice />
        </div>

        <div className="flex flex-wrap items-center gap-8 bg-paper-50 border border-paper-200 p-5">
          <div className="flex items-center gap-3">
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="lesson-published" />
            <span className="text-sm font-medium">{form.published ? "Publicado" : "Borrador"}</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="eyebrow text-ink-500">Orden</label>
            <Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="w-20" data-testid="lesson-order" />
          </div>
        </div>
      </div>
    </div>
  );
}
